/**
 * @fileoverview Read-only detail field.
 * @description A single label/value pair in a detail dialog's field grid (View User and similar
 * read-only profiles). The value may be any node, e.g. a status Badge.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { ReactNode } from 'react'

interface DetailFieldProps {
  label: string
  children: ReactNode
  'data-testid'?: string
}

/**
 * @component
 * @param {DetailFieldProps} props - The field's label and value.
 * @param {string} props.label - The field's label.
 * @param {ReactNode} props.children - The field's display value.
 * @param {string} [props['data-testid']] - QA locator for the whole field; the value gets the same id
 * with "-value" appended (e.g. view-user-email-value).
 */
function DetailField({ label, children, 'data-testid': testId }: DetailFieldProps) {
  return (
    <div data-testid={testId}>
      <p className="text-[11px] font-semibold tracking-[0.5px] text-table-header">{label}</p>
      <div
        className="mt-1 text-[13.5px] text-ink"
        data-testid={testId ? `${testId}-value` : undefined}
      >
        {children}
      </div>
    </div>
  )
}

export default DetailField
